import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../App";
import {
  Users, Clock, MapPin, AlertTriangle, RefreshCw, Activity,
  ChevronRight, Shield, Coffee, Navigation, ExternalLink, Zap,
  Eye, EyeOff, Map as MapIcon, List,
  CalendarDays, CheckCircle2, Circle, Plus, X,
  ClipboardList, StickyNote, Camera, Briefcase,
  Bell, BellOff, Settings, Sunrise
} from "lucide-react";

const API = "/api";

function fmt(seconds) {
  if (!seconds || seconds <= 0) return "0:00";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}:${String(m).padStart(2, "0")}`;
}

// ======================== MAP COMPONENT ========================

function MiniMap({ members }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);

  useEffect(() => {
    let L;
    const initMap = async () => {
      L = await import("leaflet");

      // Add CSS if not already loaded
      if (!document.querySelector('link[href*="leaflet"]')) {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
        document.head.appendChild(link);
      }

      if (!mapRef.current) return;

      if (!mapInstanceRef.current) {
        mapInstanceRef.current = L.map(mapRef.current, {
          zoomControl: false,
          attributionControl: false,
        }).setView([51.1657, 10.4515], 6); // Germany center

        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          maxZoom: 18,
        }).addTo(mapInstanceRef.current);

        L.control.zoom({ position: "topright" }).addTo(mapInstanceRef.current);
      }

      // Clear old markers
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];

      const pins = members.filter((m) => m.gps_lat && m.gps_lng);
      if (pins.length === 0) return;

      const bounds = [];
      pins.forEach((m) => {
        const color = m.gps_status === "green" ? "#16a34a" : m.gps_status === "yellow" ? "#d97706" : "#dc2626";
        const icon = L.divIcon({
          className: "custom-pin",
          html: `<div style="width:28px;height:28px;border-radius:50%;background:${color};border:3px solid white;box-shadow:0 2px 8px var(--tga-shadow-overlay, rgba(0,0,0,0.15));display:flex;align-items:center;justify-content:center;color:white;font-size:10px;font-weight:700">${m.name.split(" ").map((n) => n[0]).join("").substring(0, 2)}</div>`,
          iconSize: [28, 28],
          iconAnchor: [14, 14],
        });

        const marker = L.marker([m.gps_lat, m.gps_lng], { icon })
          .addTo(mapInstanceRef.current)
          .bindPopup(
            `<div style="font-size:12px;line-height:1.5"><strong>${m.name}</strong><br/>${m.project_name || "Kein Projekt"}<br/><span style="color:#666">${m.clock_in_local || ""}</span></div>`,
            { closeButton: false }
          );
        markersRef.current.push(marker);
        bounds.push([m.gps_lat, m.gps_lng]);
      });

      if (bounds.length > 1) {
        mapInstanceRef.current.fitBounds(bounds, { padding: [30, 30], maxZoom: 13 });
      } else if (bounds.length === 1) {
        mapInstanceRef.current.setView(bounds[0], 13);
      }
    };

    initMap();

    return () => {
      // Don't destroy map on every render, just clear markers
    };
  }, [members]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  return <div ref={mapRef} className="w-full h-full rounded-xl" style={{ minHeight: 250 }} />;
}

// ======================== MAIN COMPONENT ========================

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [agenda, setAgenda] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const [showMap, setShowMap] = useState(true);
  const [showWarnings, setShowWarnings] = useState(true);
  const [fabOpen, setFabOpen] = useState(false);
  const [showNewTask, setShowNewTask] = useState(false);
  const [showNewTermin, setShowNewTermin] = useState(false);
  const [dailySummary, setDailySummary] = useState(null);
  const [showSummarySettings, setShowSummarySettings] = useState(false);
  const navigate = useNavigate();
  const intervalRef = useRef(null);
  const tickRef = useRef(null);
  const touchStartY = useRef(null);
  const [pulling, setPulling] = useState(false);

  const load = useCallback(async () => {
    try {
      const [teamRes, agendaRes] = await Promise.all([
        authFetch(`${API}/clock/live-team`),
        authFetch(`${API}/dashboard/agenda`),
      ]);
      if (teamRes.ok) setData(await teamRes.json());
      if (agendaRes.ok) setAgenda(await agendaRes.json());
    } catch { /* silent */ }
    setLoading(false);
    setPulling(false);
  }, []);

  const loadSummary = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/notifications/daily-summary`);
      if (res.ok) setDailySummary(await res.json());
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    loadSummary();
    intervalRef.current = setInterval(load, 30000);
    tickRef.current = setInterval(() => setTick((t) => t + 1), 1000);
    return () => {
      clearInterval(intervalRef.current);
      clearInterval(tickRef.current);
    };
  }, [load]);

  // Pull-to-refresh
  const handleTouchStart = (e) => {
    if (e.currentTarget.scrollTop === 0) {
      touchStartY.current = e.touches[0].clientY;
    }
  };
  const handleTouchMove = (e) => {
    if (touchStartY.current !== null) {
      const diff = e.touches[0].clientY - touchStartY.current;
      if (diff > 60) setPulling(true);
    }
  };
  const handleTouchEnd = () => {
    if (pulling) {
      setLoading(true);
      load();
    }
    touchStartY.current = null;
    setPulling(false);
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0f1117]">
        <RefreshCw className="w-6 h-6 text-gray-500 animate-spin" />
      </div>
    );
  }

  if (!data) {
    return <div className="p-6 text-center text-gray-400 bg-[#0f1117] min-h-screen">Fehler beim Laden</div>;
  }

  const stats = data.stats || {};
  const warnings = data.warnings || [];
  const team = data.team || [];
  const active = team.filter((m) => m.active);
  const inactive = team.filter((m) => !m.active);
  const mapMembers = active.filter((m) => m.gps_lat && m.gps_lng);

  return (
    <div
      className="min-h-screen bg-[#0f1117] text-white"
      data-testid="admin-dashboard"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Pull indicator */}
      {pulling && (
        <div className="flex justify-center py-3">
          <RefreshCw className="w-5 h-5 text-blue-400 animate-spin" />
        </div>
      )}

      {/* Header */}
      <div className="sticky top-0 z-30 bg-[#0f1117]/95 backdrop-blur-md border-b border-white/5 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
              <Activity className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-sm font-bold text-white tracking-tight">Admin Dashboard</h1>
              <p className="text-[10px] text-gray-500 font-mono">Live · Auto-Refresh 30s</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setShowSummarySettings(true)}
              className="p-2 rounded-lg bg-white/5 hover:bg-white/10 active:bg-white/15 transition-colors"
              data-testid="summary-settings-btn"
            >
              <Bell className="w-4 h-4 text-gray-400" />
            </button>
            <button
              onClick={() => { setLoading(true); load(); }}
              className="p-2 rounded-lg bg-white/5 hover:bg-white/10 active:bg-white/15 transition-colors"
              data-testid="dashboard-refresh"
            >
              <RefreshCw className={`w-4 h-4 text-gray-400 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>
      </div>

      <div className="px-3 pb-24 space-y-4 pt-3">
        {/* ===== STAT TILES ===== */}
        <div className="grid grid-cols-2 gap-2.5" data-testid="dashboard-stats">
          <StatTile
            label="Aktive Mitarbeiter"
            value={stats.active_count || 0}
            sub={`von ${stats.total_count || 0}`}
            color="blue"
            icon={<Users className="w-4 h-4" />}
          />
          <StatTile
            label="Stunden heute"
            value={`${stats.total_hours_today || 0}h`}
            sub="Gesamt"
            color="green"
            icon={<Clock className="w-4 h-4" />}
          />
          <StatTile
            label="Ohne GPS"
            value={stats.no_gps_count || 0}
            sub={stats.no_gps_count > 0 ? "Achtung" : "Alles OK"}
            color={stats.no_gps_count > 0 ? "red" : "gray"}
            icon={<Navigation className="w-4 h-4" />}
          />
          <StatTile
            label="Nachunternehmer"
            value={stats.subcontractor_active || 0}
            sub="Aktiv"
            color="purple"
            icon={<Zap className="w-4 h-4" />}
          />
        </div>

        {/* ===== DAILY SUMMARY BANNER ===== */}
        {dailySummary?.show && (
          <DailySummaryBanner
            summary={dailySummary}
            onDismiss={async () => {
              await authFetch(`${API}/notifications/daily-summary/dismiss`, { method: "POST" });
              setDailySummary({ show: false });
            }}
            onSettings={() => setShowSummarySettings(true)}
          />
        )}

        {/* ===== WARNINGS ===== */}
        {warnings.length > 0 && (
          <div data-testid="dashboard-warnings">
            <button
              onClick={() => setShowWarnings((v) => !v)}
              className="flex items-center gap-2 w-full mb-2"
            >
              <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-[11px] font-semibold text-amber-400 uppercase tracking-wider">
                Warnungen ({warnings.length})
              </span>
              <ChevronRight className={`w-3 h-3 text-gray-500 ml-auto transition-transform ${showWarnings ? "rotate-90" : ""}`} />
            </button>
            {showWarnings && (
              <div className="space-y-1.5">
                {warnings.map((w, i) => (
                  <WarningCard key={i} warning={w} onNavigate={() => navigate(`/app/m/mitarbeiter/${w.user_id}/zeitanalyse`)} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ===== TERMINE & AUFGABEN ===== */}
        {agenda && (
          <AgendaSection
            agenda={agenda}
            tick={tick}
            onTaskDone={async (taskId, source) => {
              const url = source === "crm" ? `${API}/crm/tasks/${taskId}` : `${API}/tasks/${taskId}`;
              await authFetch(url, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status: "erledigt" }) });
              load();
            }}
          />
        )}

        {/* ===== MAP ===== */}
        <div data-testid="dashboard-map-section">
          <button
            onClick={() => setShowMap((v) => !v)}
            className="flex items-center gap-2 w-full mb-2"
          >
            <MapIcon className="w-3.5 h-3.5 text-blue-400" />
            <span className="text-[11px] font-semibold text-blue-400 uppercase tracking-wider">
              Karte ({mapMembers.length} Standorte)
            </span>
            <div className="ml-auto flex items-center gap-1">
              {showMap ? <EyeOff className="w-3 h-3 text-gray-500" /> : <Eye className="w-3 h-3 text-gray-500" />}
            </div>
          </button>
          {showMap && (
            <div className="rounded-xl overflow-hidden border border-white/10 bg-[#181b24]" style={{ height: 280 }}>
              {mapMembers.length > 0 ? (
                <MiniMap members={mapMembers} />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500 text-xs">
                  <MapPin className="w-4 h-4 mr-2 opacity-50" />
                  Keine GPS-Standorte verfügbar
                </div>
              )}
            </div>
          )}
        </div>

        {/* ===== ACTIVE EMPLOYEES ===== */}
        <div data-testid="dashboard-active-list">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-[11px] font-semibold text-green-400 uppercase tracking-wider">
              Eingestempelt ({active.length})
            </span>
          </div>
          {active.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-xs">Niemand eingestempelt</div>
          ) : (
            <div className="space-y-2">
              {active.map((m) => (
                <ActiveCard key={m.id} member={m} tick={tick} navigate={navigate} />
              ))}
            </div>
          )}
        </div>

        {/* ===== INACTIVE EMPLOYEES ===== */}
        <div data-testid="dashboard-inactive-list">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 rounded-full bg-gray-500" />
            <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
              Nicht eingestempelt ({inactive.length})
            </span>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            {inactive.map((m) => (
              <div
                key={m.id}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/5 cursor-pointer hover:bg-white/[0.06] transition-colors"
                onClick={() => navigate(`/app/m/mitarbeiter/${m.id}/zeitanalyse`)}
                data-testid={`dash-inactive-${m.id}`}
              >
                <div className="w-6 h-6 rounded-full bg-gray-700 flex items-center justify-center text-[8px] font-bold text-gray-400">
                  {m.name.split(" ").map((n) => n[0]).join("").substring(0, 2)}
                </div>
                <span className="text-[11px] text-gray-500 truncate">{m.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ===== FLOATING ACTION BUTTON ===== */}
      <FAB
        fabOpen={fabOpen}
        setFabOpen={setFabOpen}
        onNewTask={() => { setFabOpen(false); setShowNewTask(true); }}
        onNewTermin={() => { setFabOpen(false); setShowNewTermin(true); }}
        onNavigate={navigate}
      />

      {/* ===== INLINE FORMS ===== */}
      {showNewTask && (
        <InlineTaskForm onClose={() => setShowNewTask(false)} onSave={() => { setShowNewTask(false); load(); }} />
      )}
      {showNewTermin && (
        <InlineTerminForm onClose={() => setShowNewTermin(false)} onSave={() => { setShowNewTermin(false); load(); }} />
      )}
      {showSummarySettings && (
        <DailySummarySettingsModal
          onClose={() => setShowSummarySettings(false)}
          onSave={() => { setShowSummarySettings(false); loadSummary(); }}
        />
      )}
    </div>
  );
}

// ======================== STAT TILE ========================

function StatTile({ label, value, sub, color, icon }) {
  const colors = {
    blue: "from-blue-600/20 to-blue-600/5 border-blue-500/20 text-blue-400",
    green: "from-emerald-600/20 to-emerald-600/5 border-emerald-500/20 text-emerald-400",
    red: "from-red-600/20 to-red-600/5 border-red-500/20 text-red-400",
    purple: "from-purple-600/20 to-purple-600/5 border-purple-500/20 text-purple-400",
    gray: "from-gray-600/10 to-gray-600/5 border-gray-500/10 text-gray-400",
  };
  const c = colors[color] || colors.gray;

  return (
    <div className={`bg-gradient-to-br ${c} border rounded-xl p-3.5`} data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">{label}</span>
        {icon}
      </div>
      <p className="text-2xl font-bold tracking-tight">{value}</p>
      <p className="text-[10px] text-gray-500 mt-0.5">{sub}</p>
    </div>
  );
}

// ======================== WARNING CARD ========================

function WarningCard({ warning: w, onNavigate }) {
  const sevColor = w.severity === "red" ? "border-red-500/30 bg-red-500/5" : "border-amber-500/30 bg-amber-500/5";
  const iconColor = w.severity === "red" ? "text-red-400" : "text-amber-400";

  return (
    <div
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border ${sevColor} cursor-pointer active:opacity-80`}
      onClick={onNavigate}
      data-testid={`warning-${w.type}-${w.user_id}`}
    >
      <AlertTriangle className={`w-4 h-4 shrink-0 ${iconColor}`} />
      <p className="text-[11px] text-gray-300 flex-1">{w.message}</p>
      <ChevronRight className="w-3 h-3 text-gray-600" />
    </div>
  );
}

// ======================== ACTIVE EMPLOYEE CARD ========================

function ActiveCard({ member: m, tick, navigate }) {
  const elapsed = m.elapsed_seconds + tick;
  const gpsColors = { green: "bg-green-500", yellow: "bg-amber-400", red: "bg-red-500", none: "bg-gray-500" };
  const isOvertime = elapsed > 36000;

  // Geofence status label
  const geoLabel = m.geofence_status === "im_bereich" ? "Im Bereich"
    : m.geofence_status === "ausserhalb" ? `Ausserhalb (${m.geofence_distance || "?"}m)`
    : m.geofence_status === "signal_verloren" ? "GPS verloren"
    : m.geofence_status === "indoor_modus" ? "Indoor"
    : m.gps_status === "green" ? "GPS OK"
    : "Kein GPS";

  const geoColor = m.geofence_status === "im_bereich" ? "text-green-400"
    : m.geofence_status === "ausserhalb" ? "text-red-400"
    : m.geofence_status === "signal_verloren" ? "text-amber-400"
    : m.geofence_status === "indoor_modus" ? "text-blue-400"
    : m.gps_status === "green" ? "text-green-400"
    : "text-red-400";

  return (
    <div
      className={`rounded-xl border p-3.5 transition-all cursor-pointer active:scale-[0.99] ${
        isOvertime ? "bg-amber-500/5 border-amber-500/20" : "bg-white/[0.04] border-white/8 hover:bg-white/[0.07]"
      }`}
      onClick={() => navigate(`/app/m/mitarbeiter/${m.id}/zeitanalyse`)}
      data-testid={`dash-active-${m.id}`}
    >
      {/* Row 1: Name + GPS */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-9 h-9 rounded-full bg-blue-600/20 border border-blue-500/30 flex items-center justify-center text-[11px] font-bold text-blue-400">
            {m.name.split(" ").map((n) => n[0]).join("").substring(0, 2)}
          </div>
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-white truncate">{m.name}</p>
            {m.project_name ? (
              <p className="text-[11px] text-gray-400 truncate">{m.project_name}</p>
            ) : (
              <p className="text-[11px] text-amber-400 italic">Kein Projekt</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <span className={`w-2.5 h-2.5 rounded-full ${gpsColors[m.gps_status]}`} />
          <span className={`text-[10px] font-mono ${geoColor}`}>
            {geoLabel}
          </span>
        </div>
      </div>

      {/* Row 2: Clock + Duration + Actions */}
      <div className="flex items-center justify-between mt-2.5 pt-2 border-t border-white/5">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3 text-gray-500" />
            <span className="text-xs font-mono text-gray-400">{m.clock_in_local}</span>
          </div>
          {m.on_break && (
            <span className="flex items-center gap-0.5 text-[10px] text-amber-400 bg-amber-400/10 px-1.5 py-0.5 rounded font-medium">
              <Coffee className="w-3 h-3" /> Pause
            </span>
          )}
          {isOvertime && (
            <span className="flex items-center gap-0.5 text-[10px] text-red-400 bg-red-400/10 px-1.5 py-0.5 rounded font-medium">
              <AlertTriangle className="w-3 h-3" /> &gt;10h
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-lg font-bold font-mono tabular-nums ${isOvertime ? "text-amber-400" : "text-white"}`} data-testid={`dash-duration-${m.id}`}>
            {fmt(elapsed)}
          </span>
          <ExternalLink className="w-3.5 h-3.5 text-gray-600" />
        </div>
      </div>

      {/* Row 3: GPS Address */}
      {m.gps_address && (
        <div className="flex items-center gap-1.5 mt-1.5">
          <MapPin className="w-3 h-3 text-gray-600 shrink-0" />
          <p className="text-[10px] text-gray-500 truncate">{m.gps_address}</p>
        </div>
      )}
    </div>
  );
}


// ======================== AGENDA SECTION ========================

function AgendaSection({ agenda, tick, onTaskDone }) {
  const termine = agenda.termine_heute || [];
  const termineMorgen = agenda.termine_morgen || [];
  const aufgaben = agenda.aufgaben || [];
  const crmAufgaben = agenda.crm_aufgaben || [];
  const alleAufgaben = [
    ...aufgaben.map((a) => ({ ...a, source: a.source || "allgemein" })),
    ...crmAufgaben,
  ].sort((a, b) => {
    const prio = { dringend: 0, hoch: 1, normal: 2 };
    return (prio[a.prioritaet] ?? 2) - (prio[b.prioritaet] ?? 2);
  });

  return (
    <div className="space-y-3" data-testid="agenda-section">
      {/* HEUTE - Termine */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <CalendarDays className="w-3.5 h-3.5 text-blue-400" />
          <span className="text-[11px] font-semibold text-blue-400 uppercase tracking-wider">
            Heute ({termine.length}) {termineMorgen.length > 0 && `· Morgen ${termineMorgen.length}`}
          </span>
        </div>
        {termine.length === 0 ? (
          <div className="px-3 py-3 rounded-xl bg-white/[0.03] border border-white/5">
            <p className="text-[11px] text-gray-500">Keine Termine heute</p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {termine.map((t, i) => (
              <TerminCard key={i} termin={t} tick={tick} />
            ))}
          </div>
        )}
      </div>

      {/* AUFGABEN */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <ClipboardList className="w-3.5 h-3.5 text-amber-400" />
          <span className="text-[11px] font-semibold text-amber-400 uppercase tracking-wider">
            Aufgaben ({alleAufgaben.length} offen)
          </span>
        </div>
        {alleAufgaben.length === 0 ? (
          <div className="px-3 py-3 rounded-xl bg-white/[0.03] border border-white/5">
            <p className="text-[11px] text-gray-500">Keine offenen Aufgaben</p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {alleAufgaben.slice(0, 8).map((a) => (
              <AufgabeCard key={a.id} aufgabe={a} onDone={() => onTaskDone(a.id, a.source)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ======================== TERMIN CARD ========================

function TerminCard({ termin: t, tick }) {
  const now = new Date();
  let countdown = "";
  if (t.time_start) {
    const [h, m] = t.time_start.split(":").map(Number);
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m);
    const diff = Math.floor((start - now) / 60000);
    if (diff > 0 && diff < 480) {
      const hrs = Math.floor(diff / 60);
      const mins = diff % 60;
      countdown = hrs > 0 ? `in ${hrs}h ${mins}min` : `in ${mins}min`;
    } else if (diff <= 0 && diff > -120) {
      countdown = "jetzt";
    }
  }

  return (
    <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/[0.03] border border-white/5" data-testid={`termin-card-${t.id || 0}`}>
      <div className="text-center min-w-[42px]">
        <p className="text-sm font-bold text-white font-mono">{t.time_start || "--:--"}</p>
        {t.time_end && <p className="text-[9px] text-gray-600 font-mono">{t.time_end}</p>}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[12px] font-semibold text-white truncate">{t.title || t.titel || "Termin"}</p>
        {t.description && <p className="text-[10px] text-gray-500 truncate">{t.description}</p>}
      </div>
      {countdown && (
        <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${
          countdown === "jetzt" ? "bg-red-500/15 text-red-400" : "bg-blue-500/15 text-blue-400"
        }`}>{countdown}</span>
      )}
    </div>
  );
}

// ======================== AUFGABE CARD ========================

function AufgabeCard({ aufgabe: a, onDone }) {
  const prioColors = {
    dringend: { dot: "bg-red-400", text: "text-red-400", border: "border-red-500/20" },
    hoch: { dot: "bg-amber-400", text: "text-amber-400", border: "border-amber-500/20" },
    normal: { dot: "bg-gray-400", text: "text-gray-400", border: "border-white/5" },
  };
  const pc = prioColors[a.prioritaet] || prioColors.normal;
  const isCRM = a.source === "crm";

  return (
    <div className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-white/[0.03] border ${pc.border}`} data-testid={`aufgabe-card-${a.id}`}>
      {/* Check button */}
      <button
        onClick={(e) => { e.stopPropagation(); onDone(); }}
        className="w-6 h-6 rounded-full border-2 border-gray-600 flex items-center justify-center flex-shrink-0 active:bg-emerald-500/20 active:border-emerald-400 transition-colors"
        data-testid={`aufgabe-done-${a.id}`}
      >
        <Circle className="w-3 h-3 text-gray-600" />
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-[12px] font-semibold text-white truncate">{a.titel}</p>
          {isCRM && <span className="text-[8px] font-bold bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded flex-shrink-0">CRM</span>}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          {a.zugewiesen_name && <span className="text-[9px] text-gray-500">{a.zugewiesen_name}</span>}
          {a.kunde_name && <span className="text-[9px] text-gray-500">{a.kunde_name}</span>}
          {a.faellig_am && <span className="text-[9px] text-gray-600">{a.faellig_am}</span>}
        </div>
      </div>

      <div className={`w-2 h-2 rounded-full ${pc.dot} flex-shrink-0`} />
    </div>
  );
}

// ======================== FAB ========================

function FAB({ fabOpen, setFabOpen, onNewTask, onNewTermin, onNavigate }) {
  const actions = [
    { icon: ClipboardList, label: "Neue Aufgabe", color: "bg-amber-500", onClick: onNewTask },
    { icon: CalendarDays, label: "Neuer Termin", color: "bg-blue-500", onClick: onNewTermin },
    { icon: StickyNote, label: "Notiz", color: "bg-emerald-500", onClick: () => { onNavigate("/app/m/notizen"); setFabOpen(false); } },
    { icon: Camera, label: "Kontakt", color: "bg-purple-500", onClick: () => { onNavigate("/app/crm"); setFabOpen(false); } },
  ];

  return (
    <>
      {/* Backdrop */}
      {fabOpen && <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setFabOpen(false)} />}

      {/* Action items */}
      <div className="fixed bottom-20 right-4 z-50 flex flex-col-reverse items-end gap-2">
        {fabOpen && actions.map((a, i) => (
          <button
            key={i}
            onClick={a.onClick}
            className="flex items-center gap-2 animate-in fade-in slide-in-from-bottom-2"
            style={{ animationDelay: `${i * 50}ms` }}
            data-testid={`fab-action-${i}`}
          >
            <span className="text-[11px] font-semibold text-white bg-gray-800/90 px-2.5 py-1.5 rounded-lg shadow-lg">{a.label}</span>
            <div className={`w-10 h-10 rounded-full ${a.color} flex items-center justify-center shadow-lg`}>
              <a.icon className="w-4 h-4 text-white" />
            </div>
          </button>
        ))}
      </div>

      {/* FAB Button */}
      <button
        onClick={() => setFabOpen(!fabOpen)}
        className={`fixed bottom-20 right-4 z-50 w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-all ${
          fabOpen ? "bg-gray-700 rotate-45" : "bg-blue-600"
        }`}
        data-testid="fab-button"
      >
        {fabOpen ? <X className="w-6 h-6 text-white" /> : <Plus className="w-6 h-6 text-white" />}
      </button>
    </>
  );
}

// ======================== INLINE TASK FORM ========================

function InlineTaskForm({ onClose, onSave }) {
  const [titel, setTitel] = useState("");
  const [prio, setPrio] = useState("normal");
  const [faellig, setFaellig] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!titel.trim()) return;
    setSaving(true);
    try {
      await authFetch(`${API}/tasks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ titel, prioritaet: prio, faellig_am: faellig || null }),
      });
      onSave();
    } catch { /* silent */ }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end" onClick={onClose}>
      <div className="w-full bg-[#1a1d27] rounded-t-2xl p-4 pb-8" onClick={(e) => e.stopPropagation()} data-testid="new-task-form">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-white">Neue Aufgabe</h3>
          <button onClick={onClose} className="text-gray-400 active:text-white"><X className="w-5 h-5" /></button>
        </div>
        <input
          className="w-full bg-white/[0.06] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-500 mb-3"
          placeholder="Aufgabe eingeben..."
          value={titel}
          onChange={(e) => setTitel(e.target.value)}
          autoFocus
          data-testid="task-title-input"
        />
        <div className="flex gap-2 mb-3">
          {["dringend", "normal"].map((p) => (
            <button
              key={p}
              onClick={() => setPrio(p)}
              className={`flex-1 py-2 rounded-lg text-xs font-bold uppercase tracking-wider border transition-colors ${
                prio === p
                  ? p === "dringend" ? "bg-red-500/20 border-red-500/40 text-red-400" : "bg-gray-500/20 border-gray-500/30 text-gray-300"
                  : "bg-white/[0.03] border-white/5 text-gray-500"
              }`}
              data-testid={`task-prio-${p}`}
            >
              {p}
            </button>
          ))}
        </div>
        <input
          type="date"
          className="w-full bg-white/[0.06] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white mb-4"
          value={faellig}
          onChange={(e) => setFaellig(e.target.value)}
          data-testid="task-date-input"
        />
        <button
          onClick={handleSave}
          disabled={!titel.trim() || saving}
          className="w-full py-3 rounded-lg text-sm font-bold bg-blue-600 text-white active:bg-blue-700 disabled:bg-gray-700 disabled:text-gray-500 transition-colors"
          data-testid="task-save-btn"
        >
          {saving ? "Speichern..." : "Aufgabe erstellen"}
        </button>
      </div>
    </div>
  );
}

// ======================== INLINE TERMIN FORM ========================

function InlineTerminForm({ onClose, onSave }) {
  const [titel, setTitel] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [timeStart, setTimeStart] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!titel.trim() || !timeStart) return;
    setSaving(true);
    try {
      await authFetch(`${API}/appointments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: titel, date, time: timeStart, end_time: "" }),
      });
      onSave();
    } catch { /* silent */ }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end" onClick={onClose}>
      <div className="w-full bg-[#1a1d27] rounded-t-2xl p-4 pb-8" onClick={(e) => e.stopPropagation()} data-testid="new-termin-form">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-white">Neuer Termin</h3>
          <button onClick={onClose} className="text-gray-400 active:text-white"><X className="w-5 h-5" /></button>
        </div>
        <input
          className="w-full bg-white/[0.06] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-500 mb-3"
          placeholder="Termin Titel..."
          value={titel}
          onChange={(e) => setTitel(e.target.value)}
          autoFocus
          data-testid="termin-title-input"
        />
        <div className="flex gap-2 mb-4">
          <input
            type="date"
            className="flex-1 bg-white/[0.06] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            data-testid="termin-date-input"
          />
          <input
            type="time"
            className="w-28 bg-white/[0.06] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white"
            value={timeStart}
            onChange={(e) => setTimeStart(e.target.value)}
            data-testid="termin-time-input"
          />
        </div>
        <button
          onClick={handleSave}
          disabled={!titel.trim() || !timeStart || saving}
          className="w-full py-3 rounded-lg text-sm font-bold bg-blue-600 text-white active:bg-blue-700 disabled:bg-gray-700 disabled:text-gray-500 transition-colors"
          data-testid="termin-save-btn"
        >
          {saving ? "Speichern..." : "Termin erstellen"}
        </button>
      </div>
    </div>
  );
}


// ======================== DAILY SUMMARY BANNER ========================

function DailySummaryBanner({ summary, onDismiss, onSettings }) {
  const d = summary.data;

  return (
    <div
      className="relative rounded-xl border border-blue-500/20 bg-gradient-to-br from-blue-600/10 via-indigo-600/5 to-transparent p-4 overflow-hidden"
      data-testid="daily-summary-banner"
    >
      {/* Decorative glow */}
      <div className="absolute -top-8 -right-8 w-24 h-24 bg-blue-500/10 rounded-full blur-2xl pointer-events-none" />

      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sunrise className="w-4 h-4 text-amber-400" />
          <span className="text-xs font-bold text-white">Tageszusammenfassung</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onSettings}
            className="p-1 rounded-md hover:bg-white/10 transition-colors"
            data-testid="summary-settings-inline"
          >
            <Settings className="w-3.5 h-3.5 text-gray-500" />
          </button>
          <button
            onClick={onDismiss}
            className="p-1 rounded-md hover:bg-white/10 transition-colors"
            data-testid="summary-dismiss-btn"
          >
            <X className="w-3.5 h-3.5 text-gray-500" />
          </button>
        </div>
      </div>

      {/* Summary grid */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <SummaryItem value={d.termine_heute} label="Termine" accent="blue" />
        <SummaryItem value={d.aufgaben_offen} label="Aufgaben" accent={d.aufgaben_dringend > 0 ? "red" : "amber"} />
        <SummaryItem value={d.aktive_mitarbeiter} label="Aktiv" accent="green" />
      </div>

      {/* Extra info row */}
      <div className="flex flex-wrap gap-2 text-[10px]">
        {d.crm_offen > 0 && (
          <span className="px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-400 font-medium">
            {d.crm_offen} CRM offen
          </span>
        )}
        {d.termine_morgen > 0 && (
          <span className="px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-400 font-medium">
            Morgen: {d.termine_morgen} Termine
          </span>
        )}
        {d.warnungen.map((w, i) => (
          <span key={i} className="px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 font-medium">
            {w}
          </span>
        ))}
      </div>

      {/* Today's appointment preview */}
      {d.termine_preview?.length > 0 && (
        <div className="mt-2.5 pt-2 border-t border-white/5">
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {d.termine_preview.map((t, i) => (
              <span key={i} className="text-[10px] text-gray-400">
                <span className="text-white font-mono">{t.time || "--:--"}</span>{" "}
                <span className="text-gray-500">{t.title}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryItem({ value, label, accent }) {
  const colors = {
    blue: "text-blue-400",
    amber: "text-amber-400",
    red: "text-red-400",
    green: "text-emerald-400",
  };
  return (
    <div className="text-center">
      <p className={`text-xl font-bold ${colors[accent] || "text-white"}`}>{value}</p>
      <p className="text-[9px] text-gray-500 uppercase tracking-wider">{label}</p>
    </div>
  );
}


// ======================== DAILY SUMMARY SETTINGS MODAL ========================

function DailySummarySettingsModal({ onClose, onSave }) {
  const [enabled, setEnabled] = useState(false);
  const [time, setTime] = useState("07:00");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch(`${API}/notifications/daily-summary/settings`);
        if (res.ok) {
          const data = await res.json();
          setEnabled(data.daily_summary_enabled);
          setTime(data.daily_summary_time);
        }
      } catch { /* silent */ }
      setLoading(false);
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await authFetch(`${API}/notifications/daily-summary/settings`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ daily_summary_enabled: enabled, daily_summary_time: time }),
      });
      onSave();
    } catch { /* silent */ }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end" onClick={onClose}>
      <div className="w-full bg-[#1a1d27] rounded-t-2xl p-4 pb-8" onClick={(e) => e.stopPropagation()} data-testid="summary-settings-modal">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-blue-400" />
            <h3 className="text-sm font-bold text-white">Tageszusammenfassung</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 active:text-white"><X className="w-5 h-5" /></button>
        </div>

        {loading ? (
          <div className="py-8 text-center text-gray-500 text-xs">Laden...</div>
        ) : (
          <>
            {/* Toggle */}
            <div className="flex items-center justify-between px-3 py-3 rounded-lg bg-white/[0.04] border border-white/5 mb-3">
              <div>
                <p className="text-[12px] font-semibold text-white">Benachrichtigung aktivieren</p>
                <p className="text-[10px] text-gray-500">Stundenzettel beim Öffnen anzeigen</p>
              </div>
              <button
                onClick={() => setEnabled(!enabled)}
                className={`w-12 h-7 rounded-full transition-colors flex items-center px-0.5 ${
                  enabled ? "bg-blue-600" : "bg-gray-700"
                }`}
                data-testid="summary-toggle"
              >
                <div className={`w-6 h-6 rounded-full bg-white shadow transition-transform ${
                  enabled ? "translate-x-5" : "translate-x-0"
                }`} />
              </button>
            </div>

            {/* Time picker */}
            <div className={`px-3 py-3 rounded-lg bg-white/[0.04] border border-white/5 mb-4 transition-opacity ${
              enabled ? "opacity-100" : "opacity-40 pointer-events-none"
            }`}>
              <p className="text-[12px] font-semibold text-white mb-2">Ab wann anzeigen?</p>
              <div className="flex gap-2">
                {["06:00", "07:00", "08:00", "09:00"].map((t) => (
                  <button
                    key={t}
                    onClick={() => setTime(t)}
                    className={`flex-1 py-2 rounded-lg text-xs font-bold border transition-colors ${
                      time === t
                        ? "bg-blue-500/20 border-blue-500/40 text-blue-400"
                        : "bg-white/[0.03] border-white/5 text-gray-500"
                    }`}
                    data-testid={`summary-time-${t.replace(":", "")}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
              <div className="mt-2">
                <input
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  className="w-full bg-white/[0.06] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                  data-testid="summary-time-custom"
                />
              </div>
            </div>

            {/* Info */}
            <div className="px-3 py-2.5 rounded-lg bg-blue-500/5 border border-blue-500/10 mb-4">
              <p className="text-[10px] text-blue-400/80 leading-relaxed">
                Die Zusammenfassung zeigt: Termine heute, offene Aufgaben, aktive Mitarbeiter und Warnungen.
                Sie erscheint einmal täglich ab der gewählten Uhrzeit.
              </p>
            </div>

            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full py-3 rounded-lg text-sm font-bold bg-blue-600 text-white active:bg-blue-700 disabled:bg-gray-700 disabled:text-gray-500 transition-colors"
              data-testid="summary-settings-save"
            >
              {saving ? "Speichern..." : "Einstellungen speichern"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
